
import React, { useMemo, useEffect, useRef } from 'react';

const MysticBackground: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Réduction drastique des étoiles pour économiser le CPU (150 -> 40)
    const stars = useMemo(() => {
        return Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            size: Math.random() * 2 + 1,
            delay: Math.random() * 5,
            duration: Math.random() * 3 + 2,
            opacity: Math.random() * 0.7 + 0.3
        }));
    }, []);

    // Étoiles filantes (Très léger impact, conservé pour l'ambiance)
    const shootingStars = useMemo(() => {
        return Array.from({ length: 2 }).map((_, i) => ({
            id: i,
            top: `${Math.random() * 40}%`,
            left: `${Math.random() * 100 - 20}%`,
            delay: Math.random() * 20 + 10,
            duration: 4
        }));
    }, []);

    // --- EFFET SOURIS MAGIQUE (Mouse Trail) ---
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let lastTime = 0;
        
        const createMagicParticle = (x: number, y: number) => {
            if (!container) return;
            
            const particle = document.createElement('div');
            particle.classList.add('magic-particle');
            particle.style.left = `${x}px`;
            particle.style.top = `${y}px`;
            
            // Propriétés aléatoires
            const size = Math.random() * 4 + 2; // 2px à 6px
            particle.style.width = `${size}px`;
            particle.style.height = `${size}px`;
            
            const colors = [
                'rgba(251, 191, 36, 0.8)', // Gold
                'rgba(255, 255, 255, 0.9)', // White
                'rgba(192, 132, 252, 0.8)', // Purple
                'rgba(232, 121, 249, 0.8)'  // Pink
            ];
            particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            particle.style.boxShadow = `0 0 ${size * 2}px ${particle.style.backgroundColor}`;
            
            container.appendChild(particle);

            // Nettoyage après animation (1s)
            setTimeout(() => {
                if (particle.parentNode === container) {
                    container.removeChild(particle);
                }
            }, 1000);
        };

        const handleMouseMove = (e: MouseEvent) => {
            const now = Date.now();
            // Limiter la création à une particule toutes les 40ms pour la perf
            if (now - lastTime > 40) {
                createMagicParticle(e.clientX, e.clientY);
                lastTime = now;
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <div ref={containerRef} className="fixed inset-0 z-0 pointer-events-none overflow-hidden bg-[#0f0518]">
            {/* Arrière-plan statique (Pas de calculs JS) */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#1a0b2e] via-[#0f0518] to-black opacity-100"></div>
            
            {/* Nébuleuses statiques (CSS only - GPU accelerated) */}
            <div className="absolute top-[-20%] left-[-20%] w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[80px]"></div>
            <div className="absolute bottom-[-20%] right-[-20%] w-[600px] h-[600px] bg-indigo-900/10 rounded-full blur-[80px]"></div>

            {/* 1. Étoiles scintillantes (CSS Animations) */}
            {stars.map((star) => (
                <div
                    key={`star-${star.id}`}
                    className="absolute bg-white rounded-full"
                    style={{
                        top: star.top,
                        left: star.left,
                        width: `${star.size}px`,
                        height: `${star.size}px`,
                        opacity: star.opacity,
                        animation: `twinkle ${star.duration}s infinite ease-in-out ${star.delay}s`,
                        willChange: 'opacity' // Optimisation GPU
                    }}
                ></div>
            ))}

            {/* 2. Étoiles filantes (CSS Animations) */}
             <div className="absolute inset-0 w-full h-full overflow-hidden">
                {shootingStars.map((star) => (
                    <div
                        key={`shoot-${star.id}`}
                        className="shooting-star-container"
                        style={{
                            top: star.top,
                            left: star.left,
                            animation: `shooting-star-move ${star.duration}s linear infinite ${star.delay}s`,
                            willChange: 'transform, opacity'
                        }}
                    >
                        <div className="shooting-star-head"></div>
                        <div className="shooting-star-tail"></div>
                    </div>
                ))}
            </div>

            <style>{`
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 1; }
                }

                .shooting-star-container {
                    position: absolute;
                    width: 150px;
                    height: 2px;
                    transform: rotate(45deg);
                    opacity: 0;
                }

                .shooting-star-head {
                    position: absolute;
                    right: 0;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 2px;
                    height: 2px;
                    background: white;
                    border-radius: 50%;
                }

                .shooting-star-tail {
                    position: absolute;
                    top: 0;
                    right: 1px;
                    bottom: 0;
                    left: 0;
                    background: linear-gradient(90deg, transparent, rgba(251, 191, 36, 0.3), white);
                }

                @keyframes shooting-star-move {
                    0% { transform: translate(0, 0) rotate(45deg) scale(0.8); opacity: 0; }
                    10% { opacity: 1; transform: translate(100px, 100px) rotate(45deg) scale(1); }
                    20% { opacity: 0; }
                    100% { transform: translate(800px, 800px) rotate(45deg) scale(1); opacity: 0; }
                }

                /* --- MAGIC MOUSE PARTICLES --- */
                .magic-particle {
                    position: absolute;
                    border-radius: 50%;
                    pointer-events: none;
                    transform: translate(-50%, -50%);
                    animation: particleFade 1s ease-out forwards;
                    z-index: 9999; /* Toujours au dessus du fond mais sous l'interface */
                }

                @keyframes particleFade {
                    0% {
                        transform: translate(-50%, -50%) scale(1);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, 20px) scale(0);
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default MysticBackground;
